import { useState, type MouseEvent } from 'react';
import FormattedServiceTime from '@/components/FormattedServiceTime';
import { isNavigableAddress, openGoogleMapsNavigation } from '@/lib/openGoogleMaps';
import {
  canUserMarkServiceCompleted,
  formatOrderDurationHours,
} from '@/api/modules/order';
import { getOrderStatusMeta } from '@/constants/calendarStatus';
import { ORDER_LIST_ACTION_CLASS } from '@/constants/orderUi';
import type { Order, OrderServicerInfo } from '@/types/order';

interface OrderCardProps {
  order: Order;
  currentUserId?: string | null;
  canViewCustomer?: boolean;
  onClick: () => void;
  onComplete?: () => void;
  completeSubmitting?: boolean;
  onAssign?: () => void;
  onOrderOperation?: () => void;
  onApplyInvoice?: () => void;
  onConfirmPayment?: () => void;
  confirmPaymentSubmitting?: boolean;
  confirmPaymentLabel?: string;
  onCreateRecurring?: () => void;
  onReorder?: () => void;
}

function formatOrderRef(id: string): string {
  const trimmed = id.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function servicerInitial(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
}

function ServicerAvatar({ servicer }: { servicer: OrderServicerInfo }) {
  const [imageFailed, setImageFailed] = useState(false);
  const avatarUrl = servicer.avatar?.trim();

  if (avatarUrl && !imageFailed) {
    return (
      <img
        src={avatarUrl}
        alt={servicer.name}
        className="w-6 h-6 rounded-full object-cover object-top border-2 border-white bg-white"
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <div
      className="w-6 h-6 rounded-full bg-green-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-green-700 flex-shrink-0"
      aria-hidden
    >
      {servicerInitial(servicer.name)}
    </div>
  );
}

export function orderListPaymentLabel(label: string | undefined): string {
  if (label === 'Confirm Payment') return 'Pay';
  if (label === 'Close Order') return 'Close';
  if (label === 'Confirm & Close') return 'Pay & Close';
  return label || 'Pay';
}

export default function OrderCard({
  order,
  currentUserId = null,
  canViewCustomer = false,
  onClick,
  onComplete,
  completeSubmitting = false,
  onAssign,
  onOrderOperation,
  onApplyInvoice,
  onConfirmPayment,
  confirmPaymentSubmitting = false,
  confirmPaymentLabel,
  onCreateRecurring,
  onReorder,
}: OrderCardProps) {
  const cfg = getOrderStatusMeta(order.status);
  const servicers = order.servicers ?? [];
  const canNavigateToAddress = isNavigableAddress(order.customer.address);
  const showComplete = canUserMarkServiceCompleted(order, currentUserId);
  const showInvoice = Boolean(onApplyInvoice)
    && (order.status === 'service_done' || order.status === 'completed');
  const customerContact = [order.customer.phone, order.customer.email].filter((v) => v && v !== '—').join(' · ');
  const teamTitle = order.teamName?.trim() || 'Team';
  const orderRef = formatOrderRef(order.id);
  const showActions = Boolean(
    showComplete || showInvoice || onAssign || onOrderOperation || onConfirmPayment || onCreateRecurring || onReorder,
  );

  const handleNavigateToAddress = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    openGoogleMapsNavigation(order.customer.address);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className={`h-0.5 w-full ${cfg.dotClass}`} />

      <div className={showActions ? 'px-3 pt-3' : 'p-3'}>
        <div className="flex items-center gap-2 min-w-0 mb-2.5">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <i className="ri-team-line text-sm text-gray-300 flex-shrink-0" aria-hidden />
            <span className="text-xs font-semibold text-gray-800 truncate min-w-0">
              {teamTitle}
            </span>
            {orderRef ? (
              <span className="text-xs font-medium text-gray-400 flex-shrink-0 tabular-nums">
                {orderRef}
              </span>
            ) : null}
          </div>
          <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap border flex-shrink-0 ${cfg.badgeClass}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${cfg.dotClass}`} />
            {cfg.label}
          </span>
        </div>
        {canViewCustomer ? (
          <div className="mb-2.5 min-w-0">
            <div className="text-gray-800 font-semibold text-sm leading-tight truncate">
              {order.customer.name}
            </div>
            {customerContact ? (
              <div className="text-[11px] text-gray-400 truncate mt-0.5">{customerContact}</div>
            ) : null}
          </div>
        ) : null}

        <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2 mb-3 space-y-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-map-pin-2-line text-green-500 text-xs flex-shrink-0" />
            <span className="text-gray-500 text-[11px] truncate flex-1 min-w-0">
              {order.customer.address}
            </span>
            {canNavigateToAddress && (
              <button
                type="button"
                onClick={handleNavigateToAddress}
                className="w-6 h-6 flex items-center justify-center rounded-md border border-gray-200 bg-white text-green-600 flex-shrink-0 cursor-pointer hover:bg-green-50 transition-colors"
                aria-label="Open address in Google Maps"
              >
                <i className="ri-navigation-line text-xs" aria-hidden />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-time-line text-green-500 text-xs flex-shrink-0" />
            <FormattedServiceTime
              value={order.service_time}
              className="text-gray-700 text-[11px] font-semibold truncate"
            />
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-timer-line text-green-500 text-xs flex-shrink-0" />
            <span className="text-gray-700 text-[11px] font-semibold truncate">
              {formatOrderDurationHours(order.serviceDuration)}
            </span>
          </div>
          {order.notes?.trim() && (
            <div className="flex items-start gap-2 pt-1.5 border-t border-gray-200/80">
              <i className="ri-sticky-note-line text-amber-500 text-xs flex-shrink-0 mt-0.5" />
              <span className="text-gray-600 text-[11px] leading-relaxed line-clamp-3 whitespace-pre-wrap break-words">
                {order.notes.trim()}
              </span>
            </div>
          )}
        </div>

        <div className="pt-2.5 border-t border-gray-100 flex items-center justify-between gap-2">
          <div className="flex items-center min-w-0">
            {servicers.length > 0 ? (
              <div className="flex items-center gap-1.5">
                <i className="ri-group-line text-gray-400 text-sm flex-shrink-0" aria-hidden />
                <div className="flex -space-x-1.5 items-center">
                  {servicers.slice(0, 3).map((servicer) => (
                    <ServicerAvatar key={servicer.operatorId} servicer={servicer} />
                  ))}
                  {servicers.length > 3 && (
                    <div className="w-6 h-6 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[9px] text-gray-500 font-bold flex-shrink-0">
                      +{servicers.length - 3}
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-gray-500 font-medium tabular-nums flex-shrink-0">
                  {servicers.length} staff
                </span>
              </div>
            ) : (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <i className="ri-user-unfollow-line text-gray-300" />Unassigned
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center gap-0.5 flex-shrink-0 -my-1 -mr-1 pl-2 pr-1 py-1 text-xs font-semibold text-gray-600 cursor-pointer active:text-gray-900"
          >
            Detail
            <i className="ri-arrow-right-s-line text-base leading-none font-normal" aria-hidden />
          </button>
        </div>
      </div>

      {showActions ? (
        <div className="px-3 pb-3 pt-2 flex flex-wrap gap-1.5">
              {onAssign ? (
                <button
                  type="button"
                  className={`${ORDER_LIST_ACTION_CLASS} flex-1 justify-center`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssign();
                  }}
                >
                  <i className="ri-user-add-line text-xs" />
                  Assign
                </button>
              ) : null}
              {onOrderOperation ? (
                <button
                  type="button"
                  className={`${ORDER_LIST_ACTION_CLASS} flex-1 justify-center`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOrderOperation();
                  }}
                >
                  <i className="ri-price-tag-3-line text-xs" />
                  Price & Hours
                </button>
              ) : null}
              {showComplete ? (
                <button
                  type="button"
                  disabled={completeSubmitting}
                  className={`${ORDER_LIST_ACTION_CLASS} flex-1 justify-center disabled:opacity-60`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onComplete?.();
                  }}
                >
                  <i className={`${completeSubmitting ? 'ri-loader-4-line animate-spin' : 'ri-checkbox-circle-line'} text-xs`} />
                  {completeSubmitting ? 'Saving…' : 'Complete'}
                </button>
              ) : null}
              {showInvoice ? (
                <button
                  type="button"
                  className={`${ORDER_LIST_ACTION_CLASS} flex-1 justify-center`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onApplyInvoice?.();
                  }}
                >
                  <i className="ri-bill-line text-xs" />
                  Invoice
                </button>
              ) : null}
              {onCreateRecurring ? (
                <button
                  type="button"
                  className={`${ORDER_LIST_ACTION_CLASS} flex-1 justify-center`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateRecurring();
                  }}
                >
                  <i className="ri-repeat-line text-xs" />
                  Repeat
                </button>
              ) : null}
              {onReorder ? (
                <button
                  type="button"
                  className={`${ORDER_LIST_ACTION_CLASS} flex-1 justify-center`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onReorder();
                  }}
                >
                  <i className="ri-file-copy-line text-xs" />
                  Reorder
                </button>
              ) : null}
              {onConfirmPayment ? (
                <button
                  type="button"
                  disabled={confirmPaymentSubmitting}
                  className={`${ORDER_LIST_ACTION_CLASS} flex-1 justify-center disabled:opacity-60`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfirmPayment();
                  }}
                >
                  <i className={`${confirmPaymentSubmitting ? 'ri-loader-4-line animate-spin' : 'ri-secure-payment-line'} text-xs`} />
                  {confirmPaymentSubmitting ? 'Saving…' : orderListPaymentLabel(confirmPaymentLabel)}
                </button>
              ) : null}
        </div>
      ) : null}
    </div>
  );
}
